import './reminder-form.css'

import React, { useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import EditSelfReminderFormDialog from '@/src/components/ui/optimus/edit-self-reminder-form/EditSelfReminderFormDialog'
import EditSelfReminderFormLimitRep from '@/src/components/ui/optimus/edit-self-reminder-form/EditSelfReminderFormLimitRep'
import EditSelfReminderFormRepPatternMonthly from '@/src/components/ui/optimus/edit-self-reminder-form/EditSelfReminderFormRepPatternMonthly'
import EditSelfReminderFormRepPatternWeekly from '@/src/components/ui/optimus/edit-self-reminder-form/EditSelfReminderFormRepPatternWeekly'
import EditSelfReminderFormTimeSlot from '@/src/components/ui/optimus/edit-self-reminder-form/EditSelfReminderFormTimeSlot'

import { OptimusContext } from './OptimusContextProvider'
import { daysOfWeek, OptimusReminderMapper, OptimusReminderService } from './service'

const ReminderForm: React.FC = () => {
    // Page States
    const navigate = useNavigate()
    const location = useLocation()
    const queryParams = new URLSearchParams(decodeURIComponent(location.search))
    const {
        setReminderList,
        setIsFetchError,
        reminderList,
        setLoanInfos,
        loanInfos,
        isFetchLoanInfos,
        setIsFetchLoanInfos,
    } = useContext(OptimusContext)
    const reminderID = queryParams.get('id')

    const [targetDate, setTargetDate] = useState<number | null>(null)

    // Form States
    // Repetition Pattern
    const [monthlyRadio, setMonthlyRadio] = useState(false)
    const [weeklyRadio, setWeeklyRadio] = useState(false)
    const [date, setDate] = useState<number>(new Date().getDate())
    const [activeWeekDay, setActiveWeekDay] = useState<string>(daysOfWeek[new Date().getDay()])

    // Repetition Time Slot
    const [timeSlotEarly, setTimeSlotEarly] = useState(false)
    const [timeSlotMidday, setTimeSlotMidday] = useState(false)
    const [timeSlotLateEvening, setTimeSlotLateEvening] = useState(false)

    // Repetition Limit
    const [onceRepRadio, setOnceRepRadio] = useState(false)
    const [monthlyRepRadio, setMonthlyRepRadio] = useState(false)
    const [monthOffset, setMonthOffset] = useState<number>(1)

    // Valid Submit Button
    const [isValid, setIsValid] = useState(false)
    const [isProcessing, setIsProcessing] = useState(false)

    // Form Functions
    const incrementMonthOffset = () => {
        setMonthOffset(monthOffset + 1)
    }
    const decrementMonthOffset = () => {
        setMonthOffset(monthOffset - 1)
    }

    const onLoad = () => {
        if (!isFetchLoanInfos) {
            ;(async () => {
                const loanInfo = await OptimusReminderService.fetchLoanInfo()
                setLoanInfos(loanInfo)
                setIsFetchLoanInfos(true)
                if (typeof loanInfo?.[0]?.due_date_dom !== 'undefined') {
                    setTargetDate(loanInfo[0].due_date_dom)
                }
            })()
        } else if (typeof loanInfos?.[0]?.due_date_dom !== 'undefined') {
            setTargetDate(loanInfos[0].due_date_dom)
        }
        // Set value if state already exists
        if (reminderList.length === 0) return
        const reminderDataItem = reminderList.find((item) => item.reminder_id == reminderID)
        if (!reminderDataItem) return // No need to set initial value

        const {
            repetition_type,
            specific_time_day_of_month,
            specific_time_day_of_week,
            repetition_slot,
            repetition_limit_offset,
        } = reminderDataItem

        // Handle repetition type (Monthly or Weekly)
        repetition_type === 'Monthly'
            ? (setMonthlyRadio(true),
              setDate(specific_time_day_of_month?.[0] || new Date().getDate()))
            : (setWeeklyRadio(true),
              setActiveWeekDay(
                  daysOfWeek.find(
                      (_, i) =>
                          specific_time_day_of_week?.[Object.keys(specific_time_day_of_week)[i]]
                  ) || daysOfWeek[new Date().getDay()]
              ))

        // Handle time slots
        ;({
            EarlyMorning: () => (
                setTimeSlotEarly(true),
                setTimeSlotMidday(false),
                setTimeSlotLateEvening(false)
            ),
            MidDay: () => (
                setTimeSlotEarly(false),
                setTimeSlotMidday(true),
                setTimeSlotLateEvening(false)
            ),
            LateEvening: () => (
                setTimeSlotEarly(false),
                setTimeSlotMidday(false),
                setTimeSlotLateEvening(true)
            ),
        })[repetition_slot]?.()

        // Handle repetition limit offset
        const isOnce = (repetition_limit_offset ?? 1) <= 0
        setOnceRepRadio(isOnce)
        setMonthlyRepRadio(!isOnce)
        if (!isOnce) setMonthOffset(repetition_limit_offset ?? 1)
    }
    const onSubmit = async () => {
        const data = OptimusReminderMapper.prepareCreateReminderItemData({
            monthlyRadio,
            timeSlotEarly,
            timeSlotMidday,
            onceRepRadio,
            weeklyRadio,
            date,
            activeWeekDay,
            monthlyRepRadio,
            monthOffset,
            reminderID,
        })
        setIsProcessing(true)
        try {
            await OptimusReminderService.createReminderItem(data)
            const res = await OptimusReminderService.fetchReminderList()
            setReminderList(res?.data)
            const loanInfo = await OptimusReminderService.fetchLoanInfo()
            setLoanInfos(loanInfo)
            if (typeof loanInfo?.[0]?.due_date_dom !== 'undefined') {
                setTargetDate(loanInfo[0].due_date_dom)
            }
            setIsProcessing(false)
            navigate('/optimus')
        } catch {
            setIsProcessing(false)
            setIsFetchError(true)
        }
    }
    const onCancel = async () => {
        if (reminderID === null) {
            setReminderList([])
            setIsProcessing(false)
            navigate('/optimus')
        }
        setIsProcessing(true)
        try {
            await OptimusReminderService.deleteReminderItem(reminderID)
            setReminderList([])
            setIsProcessing(false)
            navigate('/optimus')
        } catch {
            setIsProcessing(false)
            setIsFetchError(true)
        }
    }

    // Use Effects
    useEffect(onLoad, [])
    useEffect(() => {
        setIsValid(
            (monthlyRadio || weeklyRadio) &&
                (timeSlotEarly || timeSlotMidday || timeSlotLateEvening) &&
                (onceRepRadio || monthlyRepRadio)
        )
    }, [
        monthlyRadio,
        weeklyRadio,
        timeSlotEarly,
        timeSlotMidday,
        timeSlotLateEvening,
        onceRepRadio,
        monthlyRepRadio,
        monthOffset,
        activeWeekDay,
        date,
    ])

    return (
        <div className="flex flex-col py-6 px-4 min-h-screen">
            <h1 className="text-2xl font-medium pb-4">ตั้งการแจ้งเตือนจ่าย</h1>
            <div className="flex flex-col pb-4">
                <h2 className="text-lg font-medium pb-3">ความถี่ในการเตือน</h2>
                <EditSelfReminderFormRepPatternMonthly
                    isChecked={monthlyRadio}
                    onChange={(e) => {
                        setMonthlyRadio(true)
                        setWeeklyRadio(false)
                    }}
                    targetDate={targetDate}
                    date={date}
                    setDate={setDate}
                />
                <EditSelfReminderFormRepPatternWeekly
                    isChecked={weeklyRadio}
                    onChange={(e) => {
                        setWeeklyRadio(true)
                        setMonthlyRadio(false)
                    }}
                    activeWeekDay={activeWeekDay}
                    setActiveWeekDay={setActiveWeekDay}
                />
            </div>
            <div className="flex flex-col pb-4">
                <h2 className="text-normal font-medium pb-3">ช่วงเวลา</h2>
                <EditSelfReminderFormTimeSlot
                    timeSlotEarly={timeSlotEarly}
                    setTimeSlotEarly={setTimeSlotEarly}
                    timeSlotMidday={timeSlotMidday}
                    setTimeSlotMidday={setTimeSlotMidday}
                    timeSlotLateEvening={timeSlotLateEvening}
                    setTimeSlotLateEvening={setTimeSlotLateEvening}
                />
            </div>
            <div className="flex flex-col pb-6">
                <h2 className="text-normal font-medium pb-3 primary-blue">เตือนถึงเมื่อไหร่</h2>
                <EditSelfReminderFormLimitRep
                    isCheckedOnce={onceRepRadio}
                    onClickOnce={(e) => {
                        setOnceRepRadio(true)
                        setMonthlyRepRadio(false)
                    }}
                    isCheckedMulti={monthlyRepRadio}
                    onClickMonth={(e) => {
                        setOnceRepRadio(false)
                        setMonthlyRepRadio(true)
                    }}
                    monthOffset={monthOffset}
                    onClickDecrementMonthOffset={decrementMonthOffset}
                    onClickIncrementMonthOffset={incrementMonthOffset}
                />
            </div>
            <EditSelfReminderFormDialog
                isProcessing={isProcessing}
                isValid={isValid}
                onSave={onSubmit}
                onCancel={onCancel}
            />
        </div>
    )
}

export default ReminderForm
